import { noddiAsync, URLKeys } from 'noddi-async';
import { asAdapterEnv } from 'noddi-util';
import { useMemo } from 'react';
import { useCurrentBrand } from '../stores/BrandStore/brandStore';
import type { StationaryGarageOption } from '../types/stationaryBooking';

/** How far ahead the backend probes when withCapacity is true. */
const CAPACITY_LOOKAHEAD_DAYS = 90;

type UseStationaryGaragesOptions = {
  /**
   * When false, only resolves garages (departments with a garage) — no capacity
   * probe. Use for path decisions that only need existence (e.g. skip choice →
   * address). Default true for the choice screen.
   * @default true
   */
  withCapacity?: boolean;
  /** @default true */
  enabled?: boolean;
};

/**
 * Brand stationary garages from the core booking endpoint: active departments
 * with a garage, plus sell-path bookability when withCapacity.
 */
export function useStationaryGarages(options?: UseStationaryGaragesOptions) {
  const withCapacity = options?.withCapacity ?? true;
  const { currentBrand } = useCurrentBrand();

  const {
    data: garages,
    isLoading,
    isPending,
    error
  } = noddiAsync.useGet({
    type: URLKeys.getStationaryGarages,
    input: {
      brandDomain: currentBrand,
      withCapacity,
      lookaheadDays: CAPACITY_LOOKAHEAD_DAYS,
      // Sell path: lead time + adapter live intersect when configured.
      applyBookingLeadTime: withCapacity ? true : undefined
    },
    queryConfig: {
      enabled: !!currentBrand && (options?.enabled ?? true)
    }
  });

  const garageOptions: StationaryGarageOption[] = useMemo(
    () =>
      (garages ?? []).map((garage) => ({
        serviceDepartmentId: garage.serviceDepartmentId,
        name: garage.name,
        serviceOrganizationId: garage.serviceOrganizationId,
        garageId: garage.garageId,
        adapterType: garage.adapterType ?? null,
        adapterEnv: asAdapterEnv(garage.eontyreEnv),
        hasFutureCapacity: withCapacity ? garage.isBookable : false
      })),
    [garages, withCapacity]
  );

  const serviceOrganizationIds = useMemo(
    () => [...new Set(garageOptions.map((garage) => garage.serviceOrganizationId))],
    [garageOptions]
  );

  /** First adapter-enabled garage for this brand. */
  const adapterGarage = useMemo(
    () => garageOptions.find((garage) => garage.adapterType && garage.adapterEnv) ?? null,
    [garageOptions]
  );

  // isLoading (not isPending alone): query is disabled until brand is known.
  // Capacity is part of the same request when withCapacity — one loading flag.
  const isGaragesLoading = !!currentBrand && (isLoading || isPending);

  return {
    garageOptions,
    serviceOrganizationIds,
    adapterGarage,
    /** True when the brand has at least one department with a garage. */
    hasStationaryLocations: garageOptions.length > 0,
    isLoading: isGaragesLoading,
    error: error ?? null
  };
}
